import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { SiaTranslatePipe } from '../../pipes/sia-translate.pipe';
import { SiaSizeDetectorService } from '../../services/size-detector.service';

export type SiaAccordionMode = 'default' | 'compact';

@Component({
  selector: 'sia-accordion',
  templateUrl: './sia-accordion.component.html',
  styleUrls: ['./sia-accordion.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionModule, SiaTranslatePipe, NgTemplateOutlet, MatButtonModule, MatIconModule],
  host: {
    '[class.full-screen]': 'fullScreen()',
    '[class.grid-container]': 'gridContainer()',
    // grid-container mode: make the host a min-height:0 flex column so the
    // panel chain can hand a bounded height down to the grid child. These map
    // 1:1 to the former `:host(.grid-container)` CSS rule; flex-auto is used
    // (not flex-1) to preserve `flex: 1 1 auto`. Covers full-screen too, since
    // it is also gated only on gridContainer().
    '[class.flex]': 'gridContainer()',
    '[class.flex-auto]': 'gridContainer()',
    '[class.min-h-0]': 'gridContainer()',
    '[class.flex-col]': 'gridContainer()',
  },
})
export class SiaAccordionComponent {
  private sizeDetectorService = inject(SiaSizeDetectorService);
  private windowSize = toSignal(this.sizeDetectorService.onResize$);

  icon = input<string>('');
  gridContainer = input<boolean>(false);
  fullScreen = input<boolean>(false);
  status = input<'error' | null | undefined>();
  opened = model<boolean>(false);
  title = input<string>('');
  mode = input<SiaAccordionMode>('default');
  headerInformation = input<string>('');
  footerInformation = input<string>('');

  panel = viewChild(MatExpansionPanel);

  protected panelClass = computed(() => (this.status() === 'error' ? 'mat-expansion-panel-invalid' : ''));

  private isMobile = computed(() => this.windowSize()?.isMobile ?? false);

  protected displayTitle = computed(() => {
    const t = this.title();
    if (this.isMobile() && t && t.length > 20) {
      return t.substring(0, 20).concat('...');
    }
    return t;
  });

  protected displayIcon = computed(() => this.icon() || null);

  protected bodyClass = computed(() => {
    let className = 'accordion-card-body';
    if (this.opened() || this.fullScreen()) {
      className += ' accordion-card-body--opend';
    }
    return className;
  });

  protected isCompact = computed(() => this.mode() === 'compact');

  // When gridContainer is true the panel body must give its child (a grid) a
  // real, bounded height so the grid can scroll internally instead of growing
  // to fit-content and pushing the whole CRUD into a scroll. Gated by the input
  // so every other usage of sia-accordion keeps the original `h-full` behavior.
  // overflow-x-clip (not overflow-hidden): horizontal clipping still prevents a
  // wide grid from bubbling a scroll up to the CRUD, but the vertical axis stays
  // `visible` so the first form field's floating Material label (rendered a few px
  // above the field box via a negative translate) is not clipped at the top edge.
  // `overflow: clip` is used instead of `hidden` on purpose: `overflow-x: hidden`
  // would force the computed `overflow-y` to `auto` (CSS rule) and could show a
  // stray vertical scrollbar, whereas `overflow-x: clip` leaves `overflow-y`
  // genuinely `visible`. The real vertical scroll boundary is the grid's own
  // internal scroller inside the min-h-0/flex-1 wrapper, not this body.
  protected panelBodyClass = computed(() =>
    this.gridContainer()
      ? 'md:surface-lowest flex w-full min-h-0 flex-1 flex-col content-center overflow-x-clip rounded-lg md:p-4'
      : 'md:surface-lowest flex h-full w-full flex-col content-center rounded-lg md:p-4',
  );

  constructor() {
    effect(() => {
      const p = this.panel();
      const isOpened = this.opened();
      if (p) {
        if (isOpened) {
          p.open();
        } else {
          p.close();
        }
      }
    });
  }

  onOpenedChange(value: boolean) {
    this.opened.set(value);
  }
}
